import { AlertBar } from '@civilgrid/ui/AlertBar';
import { Button, CircularProgress, Select } from '@civilgrid/ui/components';
import { useParams } from '@tanstack/react-router';
import posthog from 'posthog-js';
import { useEffect, useMemo } from 'react';
import { useForm } from 'react-hook-form';
import { useAsyncRetry } from 'react-use';
import { addDataRequestInvoice, getDataRequest, updateDataRequestInvoice } from '~/api/civilsearch';
import { editProject } from '~/api/projects2';
import type { DataRequestInvoice, DataRequestV2, Project } from '~/common/types';
import { Notify } from '~/components2/Notify';
import EditInvoice from './EditInvoice';

const ABSORB_FEES_OPTIONS: { value: Project['absorbFees']; label: string }[] = [
  { value: 'none', label: 'None' },
  { value: 'partial', label: 'Partial' },
  { value: 'full', label: 'Full' },
];

export default function Invoicing() {
  const { requestId } = useParams({ strict: false }) as { requestId: string };

  const { control, handleSubmit, formState, reset } = useForm<DataRequestInvoice>();

  const dataRequest = useAsyncRetry<DataRequestV2>(async () => {
    return await getDataRequest(requestId);
  }, [requestId]);

  // biome-ignore lint/correctness/useExhaustiveDependencies: legacy
  useEffect(() => {
    if (dataRequest.value && dataRequest.value.invoices.length > 0) {
      reset(dataRequest.value.invoices[0]);
    }
  }, [dataRequest.value]);

  const onSave = async (data: DataRequestInvoice) => {
    await updateDataRequestInvoice(requestId, data.id, data);
    dataRequest.retry();
  };

  // biome-ignore lint/correctness/useExhaustiveDependencies: legacy
  const editInvoiceEls = useMemo(() => {
    if (dataRequest.value) {
      return dataRequest.value.invoices.map((invoice) => (
        <EditInvoice
          key={invoice.id}
          request={dataRequest.value!}
          invoice={invoice}
          onInvoiceSave={() => {
            dataRequest.retry();
          }}
        />
      ));
    }
  }, [dataRequest.value]);

  if (dataRequest.loading) return <CircularProgress indeterminate />;
  if (dataRequest.error) return <div>An error occurred fetching updates.</div>;

  return (
    <div>
      <div className="space-y-2">
        {dataRequest.value?.project.absorbFees === 'partial' && (
          <AlertBar variant="warning">
            Some fees are absorbed on this project, get clarification before sending invoices
          </AlertBar>
        )}
        {dataRequest.value?.project.absorbFees === 'full' && (
          <AlertBar variant="danger">All fees absorbed on this project, do not send invoices to client</AlertBar>
        )}
        <div className="flex items-center gap-4">
          <Button
            onClick={async () => {
              await addDataRequestInvoice(requestId, {});
              dataRequest.retry();
            }}
            data-testid="invoicing-add-invoice"
          >
            Add New Invoice
          </Button>
          <div className="w-32">
            <Select
              label="Absorb fees"
              options={ABSORB_FEES_OPTIONS}
              value={dataRequest.value?.project.absorbFees}
              onValueChange={async (next) => {
                const project = dataRequest.value?.project;
                if (!project || !next) return;
                const previous = project.absorbFees;
                if (next === previous) return;
                try {
                  await editProject(project.id, { attributes: { absorbFees: next as Project['absorbFees'] } });
                  posthog.capture('admin_project_absorb_fees_changed', {
                    project_id: project.projectId,
                    previous,
                    next,
                  });
                  dataRequest.retry();
                } catch {
                  Notify.error({ message: 'Failed to update absorb fees' });
                }
              }}
              data-testid="invoicing-absorb-fees-select"
            />
          </div>
        </div>
      </div>
      <div className="my-4 flex flex-wrap space-x-2 space-y-2">{editInvoiceEls}</div>
    </div>
  );
}
